import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { compactPrice } from "@/helpers/compactPrice";
import { toneOf } from "@/components/AgentDirectory";
import ShowcaseDemandCheck from "@/components/ShowcaseDemandCheck";
import ShowcaseFitForm from "@/components/ShowcaseFitForm";
import { usePublicShowcaseItem } from "@/hooks/useShowcase";
import WantListingThumb, { thumbSource } from "@/components/WantListingThumb";
import "./ShowcaseView.scss";

export const DEFAULT_HEADLINE = "Buyers we are working with";

export const LOOK_FONTS = {
  classic: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", Times, serif',
};

export const networkLineOf = (network) => (network ? `${network} Buyer Listing Service` : "The Buyer Listing Service");

export const footLineOf = (network) =>
  [
    `Buyer Want-Listings from the ${networkLineOf(network)}`,
    "updated today",
    "No client information is shared",
    "Equal Housing Opportunity",
  ].join(" \u00b7 ");

export const firstNameOf = (name) => String(name || "").trim().split(/\s+/)[0] || "the agent";

export const lookStyleOf = (look, theme) => {
  const font = LOOK_FONTS[look] || (look === "match" && theme?.bodyFont ? `${theme.bodyFont}, sans-serif` : null);
  const style = {};
  if (font) style["--sv-font"] = font;
  if (look === "match" && theme?.accent) style["--primary-500"] = theme.accent;
  if (look === "match" && theme?.text) style.color = theme.text;
  return style;
};

export const lookClassOf = (look, layout, theme) =>
  [
    `showcase-view--look-${look || "match"}`,
    layout === "list" ? "showcase-view--list" : "",
    LOOK_FONTS[look] || (look === "match" && theme?.bodyFont) ? "showcase-view--custom-font" : "",
  ]
    .filter(Boolean)
    .join(" ");

export const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "budget", label: "Highest budget" },
];

export const budgetLine = (min, max) => {
  const low = Number(min) > 0 ? compactPrice(min) : null;
  const high = Number(max) > 0 ? compactPrice(max) : null;
  if (low && high) return `${low} to ${high}`;
  if (high) return `Up to ${high}`;
  if (low) return `From ${low}`;
  return "Budget open";
};

const plural = (count, word) => `${count} ${word}${Number(count) === 1 ? "" : "s"}`;

export const factsLine = (item) =>
  [
    Number(item.bedrooms) > 0 ? plural(item.bedrooms, "bed") : null,
    Number(item.bathrooms) > 0 ? plural(item.bathrooms, "bath") : null,
    item.propertyType || null,
  ]
    .filter(Boolean)
    .join(" \u00b7 ");

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

const glue = (text) => {
  const value = String(text || "").trim();
  if (value.includes("\u00a0")) return value;
  const at = value.lastIndexOf(" ");
  return at > 0 ? `${value.slice(0, at)} ${value.slice(at + 1)}` : value;
};

const Avatar = ({ agent }) => (
  <span className={`showcase-view__avatar${agent?.photoUrl ? "" : ` is-tone-${toneOf(agent?.name)}`}`} aria-hidden="true">
    {agent?.photoUrl ? <img src={agent.photoUrl} alt="" loading="lazy" /> : initialsOf(agent?.name)}
  </span>
);

const agentOf = (item) => item.agent || (item.agentName ? { name: item.agentName, slug: item.agentSlug } : null);

const Attribution = ({ agent, brokerageName, network }) => (
  <span className="showcase-view__attr">
    {agent?.name ? <span className="showcase-view__attr-agent">{agent.name}</span> : null}
    {brokerageName ? <span>{brokerageName}</span> : null}
    <span>{networkLineOf(network)}</span>
  </span>
);

const Media = ({ item }) =>
  thumbSource(item.map) ? (
    <span className="showcase-view__map">
      <WantListingThumb map={item.map} width={480} height={240} title={item.title} />
    </span>
  ) : (
    <span className="showcase-view__area" aria-hidden="true">
      {item.location || ""}
    </span>
  );

const ShowcaseCard = ({ item, index, onOpen, brokerageName, network }) => {
  const agent = agentOf(item);
  const facts = factsLine(item);
  const body = (
    <>
      <Media item={item} />
      <span className="showcase-view__body">
        <span className="showcase-view__price">{budgetLine(item.budgetMin, item.budgetMax)}</span>
        <span className="showcase-view__title">{item.title}</span>
        {facts ? <span className="showcase-view__facts">{facts}</span> : null}
        {item.location ? <span className="showcase-view__place">{item.location}</span> : null}
        <span className="showcase-view__card-foot">
          <span className="showcase-view__agent">
            {agent ? <Avatar agent={agent} /> : null}
            <Attribution agent={agent} brokerageName={brokerageName} network={network} />
          </span>
        </span>
        <span className="showcase-view__cta">
          Contact&nbsp;{firstNameOf(agent?.name)}
          <ArrowRight size={15} strokeWidth={1.75} aria-hidden="true" />
        </span>
      </span>
    </>
  );
  const style = { "--i": Math.min(index || 0, 11) };
  return onOpen ? (
    <button type="button" className="showcase-view__card" style={style} onClick={() => onOpen(item.wantAdId)}>
      {body}
    </button>
  ) : (
    <div className="showcase-view__card" style={style}>
      {body}
    </div>
  );
};

const listText = (value) =>
  Array.isArray(value)
    ? value
        .map((part) => String(part ?? "").trim())
        .filter(Boolean)
        .join(", ")
    : value;

export const detailFactsOf = (item) =>
  [
    { label: "Nice-to-haves", value: listText(item.niceToHaves) },
    { label: "Timing", value: item.timeline },
    { label: "Financing", value: item.financing },
    { label: "Lot size", value: item.lotSize },
    { label: "Square feet", value: item.squareFeet },
  ].filter((fact) => fact.value !== undefined && fact.value !== null && String(fact.value).trim() !== "");

const DetailFacts = ({ item }) => {
  const facts = detailFactsOf(item);
  if (!facts.length) return null;
  return (
    <dl className="showcase-view__detail-facts">
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
};

const ShowcaseDetail = ({ showcaseKey, summary, onBack, brokerageName, network, autoContact, stickyContact }) => {
  const query = usePublicShowcaseItem(showcaseKey, summary.wantAdId);
  const item = { ...summary, ...(query.data || {}) };
  const agent = agentOf(item);
  const headingRef = useRef(null);
  const asideRef = useRef(null);
  const [contactOpen, setContactOpen] = useState(Boolean(autoContact));
  const facts = factsLine(item);
  const price = budgetLine(item.budgetMin, item.budgetMax);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: false });
  }, [summary.wantAdId]);

  const openContact = () => {
    setContactOpen(true);
    asideRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  return (
    <article className="showcase-view__detail" aria-labelledby="showcase-detail-title">
      <div className="showcase-view__detail-main">
        <button type="button" className="showcase-view__back" onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
          All buyers
        </button>
        <p className="showcase-view__price showcase-view__price--large">{price}</p>
        <h2 id="showcase-detail-title" ref={headingRef} tabIndex={-1}>
          {glue(item.title)}
        </h2>
        {facts || item.location ? (
          <p className="showcase-view__facts">{[facts, item.location].filter(Boolean).join(" \u00b7 ")}</p>
        ) : null}
        {thumbSource(item.map) ? (
          <div className="showcase-view__map showcase-view__map--detail">
            <WantListingThumb map={item.map} width={880} height={360} title={item.title} />
          </div>
        ) : null}
        {!query.isLoading && item.description ? <p className="showcase-view__description">{item.description}</p> : null}
        {query.isLoading ? (
          <div className="showcase-view__detail-skeleton" aria-busy="true" aria-label="Loading the details">
            <span />
            <span />
            <span />
            <span />
            <div className="showcase-view__detail-skeleton-facts">
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : (
          <DetailFacts item={item} />
        )}
      </div>
      {agent ? (
        <aside className="showcase-view__detail-aside" ref={asideRef} aria-label={`Contact ${agent.name || "the agent"}`}>
          <div className="showcase-view__contact-card">
            <div className="showcase-view__agent showcase-view__agent--detail">
              <Avatar agent={agent} />
              <Attribution agent={agent} brokerageName={brokerageName} network={network} />
            </div>
            {agent.slug ? (
              <ShowcaseFitForm
                agent={agent}
                wantAdId={item.wantAdId}
                title={item.title}
                showcaseKey={showcaseKey}
                autoOpen={contactOpen}
                onOpen={() => setContactOpen(true)}
              />
            ) : null}
          </div>
        </aside>
      ) : null}
      {stickyContact && agent?.slug && !contactOpen ? (
        <div className="showcase-view__contact-bar">
          <span className="showcase-view__contact-bar-price">{price}</span>
          <button type="button" className="showcase-view__button" onClick={openContact}>
            Contact&nbsp;{firstNameOf(agent.name)}
          </button>
        </div>
      ) : null}
    </article>
  );
};

const Skeleton = () => (
  <div className="showcase-view__grid" aria-hidden="true">
    {[0, 1, 2].map((index) => (
      <div key={index} className="showcase-view__card showcase-view__card--skeleton">
        <span className="showcase-view__skeleton-media" />
        <span className="showcase-view__skeleton-lines">
          <span />
          <span />
          <span />
        </span>
      </div>
    ))}
  </div>
);

const OwnerHead = ({ owner }) => (
  <div className="showcase-view__owner showcase-view__brand">
    <Avatar agent={{ name: owner.name, photoUrl: owner.photoUrl }} />
    <span>
      <span className="showcase-view__owner-name">{owner.name}</span>
      {owner.company ? <span className="showcase-view__owner-company">{owner.company}</span> : null}
    </span>
  </div>
);

const ShowcaseView = ({
  brokerage,
  owner = null,
  headline,
  items,
  sort,
  onSort,
  loading,
  hasMore,
  loadingMore,
  onMore,
  buyerFormUrl,
  showcaseKey,
  openId,
  onOpenChange,
  preview,
  network,
  look = "match",
  layout = "grid",
  theme = null,
  autoContact = false,
  demandCheck = false,
  stickyContact = false,
  host = "",
  confirmToken = "",
  onConfirmHandled,
}) => {
  const list = items || [];
  const open = openId ? list.find((item) => item.wantAdId === openId) || (loading ? null : { wantAdId: openId }) : null;
  // Once shown, the demand check stays mounted (hidden while a card is open) so the address, result and form survive.
  const showCheck = useRef(false);
  if (!preview && showcaseKey && (demandCheck || confirmToken)) showCheck.current = true;
  const canOpen = !preview && Boolean(showcaseKey) && Boolean(onOpenChange);

  return (
    <div
      className={`showcase-view ${lookClassOf(look, layout, theme)}${preview ? " showcase-view--preview" : ""}`}
      style={lookStyleOf(look, theme)}
    >
      <header className="showcase-view__head">
        {owner?.type === "agent" || brokerage?.logoUrl || brokerage?.name || loading || items == null ? (
          <div className="showcase-view__brand-slot">
            {owner?.type === "agent" ? (
              <OwnerHead owner={owner} />
            ) : brokerage?.logoUrl ? (
              <img className="showcase-view__logo showcase-view__brand" src={brokerage.logoUrl} alt={brokerage?.name || ""} />
            ) : brokerage?.name ? (
              <p className="showcase-view__brokerage showcase-view__brand">{brokerage.name}</p>
            ) : null}
          </div>
        ) : null}
        {open ? null : (
          <div className="showcase-view__title-row">
            <div>
              <h1>{glue(headline || DEFAULT_HEADLINE)}</h1>
              <p className="showcase-view__lede">
                Own a home that could suit one of these buyers? Contact the agent named on the&nbsp;card.
              </p>
            </div>
            {list.length || loading || items == null ? (
              <div className="showcase-view__sort" role="radiogroup" aria-label="Sort buyers">
                {SORTS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={sort === option.value}
                    className={sort === option.value ? "is-on" : ""}
                    disabled={!list.length}
                    onClick={() => onSort && sort !== option.value && onSort(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </header>

      {showCheck.current && !preview && showcaseKey ? (
        <div hidden={Boolean(open)}>
          <ShowcaseDemandCheck
            showcaseKey={showcaseKey}
            brokerageName={owner?.company || brokerage?.name}
            host={host}
            confirmToken={confirmToken}
            onConfirmHandled={onConfirmHandled}
          />
        </div>
      ) : null}

      {open ? (
        <ShowcaseDetail
          showcaseKey={showcaseKey}
          summary={open}
          brokerageName={owner?.company || brokerage?.name}
          network={network}
          autoContact={autoContact}
          stickyContact={stickyContact}
          onBack={() => onOpenChange(null)}
        />
      ) : (
        <>
          {loading && !list.length ? (
            <Skeleton />
          ) : list.length ? (
            <div className="showcase-view__grid">
              {list.map((item, index) => (
                <ShowcaseCard
                  key={item.wantAdId}
                  item={item}
                  index={index}
                  onOpen={canOpen ? onOpenChange : null}
                  brokerageName={owner?.company || brokerage?.name}
                  network={network}
                />
              ))}
            </div>
          ) : (
            <div className="showcase-view__empty">
              <div className="showcase-view__empty-copy">
                <h2>Tell us what you&apos;re looking&nbsp;for</h2>
                <p>
                  Share the home you want and {owner?.type === "agent" ? owner.name : `an agent from ${brokerage?.name || "our office"}`} will reach out with homes
                  that&nbsp;fit.
                </p>
                {buyerFormUrl ? (
                  <a className="showcase-view__button" href={buyerFormUrl} target="_blank" rel="noopener noreferrer">
                    Share what you&nbsp;want
                  </a>
                ) : null}
              </div>
              <div className="showcase-view__empty-ghost" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {hasMore ? (
            <div className="showcase-view__more">
              <button type="button" className="showcase-view__button showcase-view__button--outline" disabled={loadingMore} onClick={onMore}>
                {loadingMore ? "Loading…" : "Show more buyers"}
              </button>
            </div>
          ) : null}
        </>
      )}

      <footer className="showcase-view__foot">
        <span>{footLineOf(network)}</span>
        <a className="showcase-view__powered" href="https://www.gitcha.com" target="_blank" rel="noopener noreferrer">
          Powered by Gitcha
        </a>
      </footer>
    </div>
  );
};

export default ShowcaseView;
